import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontBodoni, fontIndivisibleVariable, theme } from "shared/lib/styles";

import { GenericArticle } from "scmp-app/components/article/article-render/generic-article";
import {
  HeaderContainer,
  Headline,
  SubHeadline,
  TopicAndSection,
} from "scmp-app/components/article/article-render/generic-article/styles";
import { PhotoEssayAndLongReadArticle } from "scmp-app/components/article/article-render/photo-essay-and-long-read-article";
import { SectionLink } from "scmp-app/components/article/article-sections/styles";
import { StyledTopicLink } from "scmp-app/components/article/article-topic/styles";
import { Flag } from "scmp-app/components/content/content-headline/content-headline-tag/styles";

import PostMagazineSvg from "./logo-post-magazine.svg";

const postMagazineStyle = () => css`
  ${HeaderContainer} {
    grid:
      "banner" min-content
      "topic-and-section" min-content
      "columnist" min-content
      "headline" min-content
      "sub-headline" min-content
      "sponsor" min-content
      "reading-time" min-content
      "google-news-button" min-content
      / 100%;

    ${theme.breakpoints.up("tablet")} {
      grid:
        "banner" min-content
        "topic-and-section" min-content
        "columnist" min-content
        "headline" min-content
        "sub-headline" min-content
        "sponsor" min-content
        "reading-time" min-content
        "action-widgets" min-content
        / 100%;
    }

    ${theme.breakpoints.up("desktop")} {
      grid:
        ". banner . ." min-content
        ". topic-and-section . ." min-content
        ". columnist . ." min-content
        ". headline . ." min-content
        ". sub-headline . ." min-content
        ". sponsor . ." min-content
        ". reading-time . ." min-content
        ". action-widgets . ." min-content
        / minmax(0, 60px) 904px 92px minmax(0, 60px);
    }

    ${SubHeadline} {
      margin-block-start: 24px;

      color: #111111;
      font-weight: 350;
      font-size: 15px;
      font-family: ${fontIndivisibleVariable};
      line-height: 140%;
      letter-spacing: -0.36px;
      font-variant-numeric: lining-nums proportional-nums;

      ${theme.breakpoints.up("tablet")} {
        margin-block-start: 20px;

        font-weight: 400;
        font-size: 18px;
        line-height: 144%;
      }
    }

    ${Headline} {
      margin-block-start: 24px;

      color: #111111;
      font-size: 26px;
      font-family: ${fontBodoni};
      line-height: 120%;
      letter-spacing: -0.78px;
      ${Flag} {
        color: #111111;
      }

      ${theme.breakpoints.up("tablet")} {
        margin-block-start: 20px;

        font-size: 32px;
        line-height: 110%;
        letter-spacing: -0.96px;
      }

      ${theme.breakpoints.up("desktop")} {
        font-size: 48px;
        letter-spacing: -1.44px;
      }
    }

    ${TopicAndSection} {
      ${theme.breakpoints.up("tablet")} {
        margin-block-start: 32px;
      }

      ${theme.breakpoints.up("desktop")} {
        margin-block-start: 40px;
      }

      ${StyledTopicLink} {
        color: #111111;
        font-weight: 400;
        font-size: 11px;
        font-family: ${fontIndivisibleVariable};
        font-variation-settings: "wght" 700;

        font-style: normal;
        line-height: 120%;
        letter-spacing: -0.22px;
        text-transform: uppercase;

        ${theme.breakpoints.up("tablet")} {
          font-variation-settings: "wght" 900;

          font-size: 13px;
          letter-spacing: -0.26px;
        }
      }

      ${SectionLink} span {
        color: #111111;
        font-weight: 400;
        font-size: 11px;
        font-family: ${fontIndivisibleVariable};
        ${theme.breakpoints.up("tablet")} {
          font-size: 13px;
          line-height: 120%;
          letter-spacing: -0.13px;
        }
      }
    }
  }
`;

export const StyledPhotoEssayAndLongReadArticle = styled(PhotoEssayAndLongReadArticle)`
  ${postMagazineStyle()}
`;

export const StyledGenericArticle = styled(GenericArticle)`
  ${postMagazineStyle()}
`;

export const BannerContainer = styled.div`
  grid-area: banner;

  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-block-start: 36px;
  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
    padding-block-end: 20px;
    border-block-end: 1px solid #777777;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-block-start: 60px;
    padding-block-end: 20px;
  }
`;

export const StyledLogo = styled(PostMagazineSvg)`
  inline-size: 120px;
  block-size: 29.073px;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 270px;
    block-size: 65.6px;
  }
`;

export const RightBannerText = styled.span`
  font-weight: 400;
  font-size: 11px;
  font-family: ${fontBodoni};
  line-height: 110%;
  letter-spacing: -0.22px;
  text-align: end;
  text-transform: uppercase;
  font-variant-numeric: lining-nums proportional-nums;

  ${theme.breakpoints.up("tablet")} {
    font-size: 17px;
    line-height: 120%;
    letter-spacing: -0.34px;
  }
`;

export const RightBanner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  inline-size: 83px;
  padding-inline-start: 12px;
  box-sizing: content-box;
  border-inline-start: 1px solid #777777;

  ${RightBannerText}:nth-of-type(1) {
    margin-inline-end: 2px;
  }

  ${RightBannerText}:nth-of-type(3) {
    grid-column: span 2;
  }

  ${theme.breakpoints.up("tablet")} {
    display: grid;
    grid-template-columns: 1fr;

    inline-size: 116px;
    padding-inline-start: 18px;
    ${RightBannerText}:nth-of-type(1) {
      margin-inline-end: 0;
    }

    ${RightBannerText}:nth-of-type(3) {
      grid-column: unset;
    }
  }
`;
